var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, isValidElement, useEffect, useMemo, useState, } from "react";
import some from "lodash/some";
import { isActionMenuItem } from "./ActionMenuItem";
import { StyledActionMenu } from "./ActionMenu.style";
import ActionMenuContext from "./ActionMenuContext";
import { useFixFocusVisible, useHandleCircularUpDown } from "./Menu";
import { trapEvent } from "../../utils/EventHelpers";
import useUniqueId from "../../hooks/useUniqueId";
import { isActionSubMenu } from "./ActionSubMenu";
import FloatingContainer from "../floating-container/FloatingContainer";
import useQuickFind from "./quick-find/useQuickFind";
import QuickFind from "./quick-find/QuickFind";
import { QUICK_FIND_HEIGHT } from "./quick-find/QuickFind.style";
import useIdAttrs from "../../hooks/useIdAttrs";
const prefixId = "nx-actionmenu-";
const getChildHasIcon = (child) => { var _a; return isValidElement(child) && (isActionMenuItem(child) || isActionSubMenu(child)) && Boolean((_a = child.props) === null || _a === void 0 ? void 0 : _a.iconName); };
const getIdFromChild = (child) => isValidElement(child) && (isActionMenuItem(child) || isActionSubMenu(child)) ? child.props.id : "";
const ActionMenu = (props) => {
    const { id: defaultId, "data-insights": insightsAttr, children, "aria-label": ariaLabel, onSelect, handleQuickFind, onHide, maxHeight = "40vh" } = props, rest = __rest(props, ["id", "data-insights", "children", "aria-label", "onSelect", "handleQuickFind", "onHide", "maxHeight"]);
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const { ref: quickFindRef, position: quickFindPosition, onShiftTab: quickFindOnShiftTab } = useQuickFind(props);
    useFixFocusVisible(id);
    const childrenNotDisabled = Children.toArray(children).filter((child) => (isActionMenuItem(child) && !child.props.disabled) || isActionSubMenu(child));
    const menuIndexes = useMemo(() => childrenNotDisabled.map(getIdFromChild), [children]);
    const atLeastOneItemWithIcon = useMemo(() => some(childrenNotDisabled.map(getChildHasIcon)), [children]);
    const [focusItem, setFocusItem] = useState("");
    const handleUpAndDown = useHandleCircularUpDown(menuIndexes, focusItem, setFocusItem, true);
    const handleMenuOnKeyDown = (event) => {
        if (event.key === "Escape") {
            onHide === null || onHide === void 0 ? void 0 : onHide();
        }
        quickFindOnShiftTab(event, () => setFocusItem(""));
        handleUpAndDown(event);
    };
    const handleQuickFindOnKeyDown = (event) => {
        if (event.key === "Tab" && !event.shiftKey) {
            trapEvent(event);
            // move focus to the first menu item
            setFocusItem(menuIndexes[0]);
        }
        else {
            handleUpAndDown(event);
            if (event.key === "Escape") {
                onHide === null || onHide === void 0 ? void 0 : onHide();
            }
        }
    };
    useEffect(() => {
        var _a;
        focusItem === "" && !handleQuickFind && ((_a = document.getElementById(id)) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true }));
    }, [focusItem]);
    const handleSelectedItem = (menuItemId, event) => {
        trapEvent(event);
        if (event && onSelect) {
            onSelect(menuItemId, event);
            onHide === null || onHide === void 0 ? void 0 : onHide();
        }
    };
    const contextValue = useMemo(() => ({
        focusItem,
        setSelectedItem: handleSelectedItem,
        setFocusItem,
        atLeastOneItemWithIcon,
        onHide,
    }), [focusItem, atLeastOneItemWithIcon, onHide]);
    const quickFind = (React.createElement(QuickFind, Object.assign({ ref: quickFindRef, key: "quick-find" }, idAttrs("-quickfind"), { quickFindPosition: quickFindPosition, onSearch: handleQuickFind, onKeyDown: handleQuickFindOnKeyDown })));
    const { tooltipPosition: _, quickFindPosition: __ } = rest, restActionMenuProps = __rest(rest, ["tooltipPosition", "quickFindPosition"]);
    return (React.createElement(FloatingContainer, null,
        quickFindPosition === "top" && quickFind,
        React.createElement(StyledActionMenu, Object.assign({}, idAttrs(), { tabIndex: 0, "aria-label": ariaLabel, onKeyDown: handleMenuOnKeyDown, "$maxHeight": handleQuickFind ? `calc(${maxHeight} - ${QUICK_FIND_HEIGHT}px)` : maxHeight, role: "menu" }, restActionMenuProps),
            React.createElement(ActionMenuContext.Provider, { value: contextValue }, children)),
        quickFindPosition === "bottom" && quickFind));
};
export default ActionMenu;
//# sourceMappingURL=ActionMenu.js.map